/**
 * Webtech HR Engine™ - Page Évaluations Manager
 * Liste des évaluations à faire par le Manager pour son équipe.
 * Accès simplifié aux campagnes et aux formulaires d'évaluation.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Users,
  Star,
  Clock,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  Calendar,
  Target,
  TrendingUp,
  FileText,
  ArrowLeft,
  Loader2,
  ClipboardList,
  Play,
  RefreshCw,
  Download
} from 'lucide-react';

// Helper — download an evaluation PDF (used by the Manager card when status = validated/completed)
const downloadEvaluationPDF = async (evaluationId, member, e) => {
  e?.stopPropagation();
  try {
    toast.info('Génération du PDF en cours...');
    const response = await api.get(
      `/performance-eval/evaluations/${evaluationId}/pdf`,
      { responseType: 'blob' }
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const today = new Date().toISOString().split('T')[0];
    link.download = `Evaluation_${member?.firstName || ''}_${member?.lastName || ''}_${today}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('PDF téléchargé');
  } catch (err) {
    let msg = 'Impossible de télécharger le PDF';
    try {
      const text = await err?.response?.data?.text?.();
      if (text) {
        const parsed = JSON.parse(text);
        msg = parsed?.detail || msg;
      }
    } catch {
      msg = err?.response?.data?.detail || msg;
    }
    toast.error(msg);
  }
};

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    'pending': { label: 'En attente', class: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
    'in_progress': { label: 'En cours', class: 'bg-blue-100 text-blue-700 border-blue-200', icon: Play },
    'completed': { label: 'Terminée', class: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
    'waiting_manager': { label: 'À évaluer', class: 'bg-purple-100 text-purple-700 border-purple-200', icon: Star },
    'not_started': { label: 'Non démarrée', class: 'bg-slate-100 text-slate-700 border-slate-200', icon: Clock }
  };
  const c = config[status] || config['pending'];
  const Icon = c.icon;
  
  return (
    <Badge className={`${c.class} border text-xs flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {c.label}
    </Badge>
  );
};

// Visual workflow indicator
const WorkflowSteps = ({ selfStatus, managerStatus }) => {
  const steps = [
    { id: 'objectives', label: 'Objectifs', done: true },
    { id: 'self', label: 'Auto-éval', done: selfStatus === 'completed' },
    { id: 'manager', label: 'Manager', done: managerStatus === 'completed' },
    { id: 'synthesis', label: 'Synthèse', done: selfStatus === 'completed' && managerStatus === 'completed' }
  ];
  
  const currentStep = !steps[1].done ? 1 : !steps[2].done ? 2 : !steps[3].done ? 3 : 4;
  
  return (
    <div className="flex items-center gap-1 mt-3">
      {steps.map((step, idx) => (
        <div key={step.id} className="flex items-center">
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all ${
            step.done 
              ? 'bg-green-100 text-green-700' 
              : idx === currentStep - 1 
                ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-300' 
                : 'bg-slate-100 text-slate-400'
          }`}>
            {step.done ? (
              <CheckCircle2 className="w-3 h-3" />
            ) : idx === currentStep - 1 ? (
              <Play className="w-3 h-3" />
            ) : (
              <Clock className="w-3 h-3" />
            )}
            <span className="hidden sm:inline">{step.label}</span>
          </div>
          {idx < steps.length - 1 && (
            <div className={`w-4 h-0.5 mx-0.5 ${
              step.done ? 'bg-green-300' : 'bg-slate-200'
            }`} />
          )}
        </div>
      ))}
    </div>
  );
};

// Evaluation card for a team member - Enhanced version
const TeamMemberEvalCard = ({ member, campaign, onEvaluate }) => {
  const getInitials = () => {
    return `${member.firstName?.[0] || ''}${member.lastName?.[0] || ''}`.toUpperCase();
  };
  
  const selfEvalStatus = member.selfEvaluationStatus || 'pending';
  const managerStatus = member.managerStatus || 'pending';
  const isWaitingForSelf = selfEvalStatus !== 'completed';
  const isManagerTurn = selfEvalStatus === 'completed' && managerStatus !== 'completed';
  const isComplete = selfEvalStatus === 'completed' && managerStatus === 'completed';
  
  // Calculate overall progress
  const progress = isComplete ? 100 : selfEvalStatus === 'completed' ? 50 : 0;
  
  // Determine card style based on status
  const cardStyle = isManagerTurn 
    ? 'border-2 border-blue-300 bg-blue-50/30 shadow-md'
    : isComplete 
      ? 'border-green-200 bg-green-50/30' 
      : '';
  
  return (
    <Card className={`hover:shadow-lg transition-all ${cardStyle}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Avatar with status ring */}
          <div className="relative">
            <Avatar className={`h-14 w-14 border-2 shadow-sm ${
              isComplete ? 'border-green-400' : isManagerTurn ? 'border-blue-400' : 'border-white'
            }`}>
              <AvatarImage src={member.photoUrl} />
              <AvatarFallback className={`text-white font-semibold ${
                isComplete 
                  ? 'bg-gradient-to-br from-green-500 to-green-700'
                  : isManagerTurn 
                    ? 'bg-gradient-to-br from-blue-500 to-blue-700'
                    : 'bg-gradient-to-br from-slate-400 to-slate-600'
              }`}>
                {getInitials()}
              </AvatarFallback>
            </Avatar>
            {isManagerTurn && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                <Star className="w-3 h-3 text-white" />
              </div>
            )}
            {isComplete && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-900">
                {member.firstName} {member.lastName}
              </h3>
              {isManagerTurn && (
                <Badge className="bg-blue-500 text-white text-xs animate-pulse">
                  À votre tour
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-500">{member.jobTitle}</p>
            
            {/* Visual workflow */}
            <WorkflowSteps selfStatus={selfEvalStatus} managerStatus={managerStatus} />
          </div>
          
          {/* Action button */}
          <div className="flex flex-col items-end gap-2">
            {isManagerTurn ? (
              <Button 
                onClick={() => onEvaluate(member)}
                className="bg-blue-600 hover:bg-blue-700 shadow-md"
              >
                <Star className="w-4 h-4 mr-2" />
                Évaluer maintenant
              </Button>
            ) : isComplete ? (
              <div className="flex flex-col gap-2 items-end">
                <Button 
                  variant="outline"
                  onClick={() => onEvaluate(member)}
                  className="border-green-300 text-green-700 hover:bg-green-50"
                  data-testid={`view-eval-${member.id}`}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Voir le bilan
                </Button>
                {member.canDownloadPdf && member.evaluationId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => downloadEvaluationPDF(member.evaluationId, member, e)}
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    data-testid={`download-pdf-${member.id}`}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger PDF
                  </Button>
                )}
              </div>
            ) : (
              <Button 
                variant="outline"
                disabled
                className="opacity-60"
              >
                <Clock className="w-4 h-4 mr-2" />
                En attente
              </Button>
            )}
            
            {/* Progress indicator */}
            <div className="text-right">
              <span className={`text-lg font-bold ${
                isComplete ? 'text-green-600' : isManagerTurn ? 'text-blue-600' : 'text-slate-400'
              }`}>
                {progress}%
              </span>
            </div>
          </div>
        </div>
        
        {/* Message contextuel */}
        {isWaitingForSelf && !isComplete && (
          <div className="mt-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-xs text-amber-700 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              En attente de l'auto-évaluation de l'employé
            </p>
          </div>
        )}
        
        {isManagerTurn && (
          <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-blue-700 flex items-center gap-1">
              <Star className="w-3 h-3" />
              L'auto-évaluation est terminée. C'est à vous d'évaluer !
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Campaign section
const CampaignSection = ({ campaign, participants, onEvaluate }) => {
  const pendingCount = participants.filter(p => 
    p.managerStatus === 'pending' || !p.managerStatus || p.managerStatus === 'not_started'
  ).length;
  const completedCount = participants.filter(p => p.managerStatus === 'completed').length;
  
  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Target className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-lg">{campaign.name}</CardTitle>
              <CardDescription>
                {campaign.startDate && (
                  <span>Du {new Date(campaign.startDate).toLocaleDateString('fr-FR')} </span>
                )}
                {campaign.endDate && (
                  <span>au {new Date(campaign.endDate).toLocaleDateString('fr-FR')}</span>
                )}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900">{completedCount}/{participants.length}</p>
              <p className="text-xs text-slate-500">évaluations</p>
            </div>
            {pendingCount > 0 && (
              <Badge className="bg-amber-100 text-amber-700">
                {pendingCount} à faire
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2">
          {participants.map((participant) => (
            <TeamMemberEvalCard
              key={participant.employeeId || participant.id}
              member={participant}
              campaign={campaign}
              onEvaluate={onEvaluate}
            />
          ))}
        </div>
        
        {participants.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>Aucun collaborateur à évaluer dans cette campagne</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main page component
export default function ManagerEvaluationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPro, loading: planLoading } = usePlan();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [evaluationsMap, setEvaluationsMap] = useState({});
  const [activeTab, setActiveTab] = useState('pending');
  
  // Load data
  const fetchData = useCallback(async () => {
    if (!user?.id || !user?.companyId) return;
    
    try {
      // Get team members from manager API
      const teamRes = await api.get('/manager/team').catch(() => ({ data: { members: [] } }));
      const members = teamRes.data?.members || [];
      setTeamMembers(members);
      
      // Get team evaluations (campaigns with participants) - OLD system
      const evalsRes = await api.get('/manager/team/evaluations').catch(() => ({ data: { campaigns: [], pendingCount: 0, completedCount: 0 } }));
      const campaignsWithParticipants = evalsRes.data?.campaigns || [];
      
      // Get performance evaluations - NEW system
      // We fetch BOTH endpoints :
      //  - pending-evaluations : returns evaluations awaiting manager action (auto-évaluations soumises, proxy)
      //  - team-evaluations    : returns ALL evaluations of the manager's team (incl. validated/completed → for PDF download)
      const [pendingRes, teamPerfRes] = await Promise.all([
        api.get('/performance-eval/manager/pending-evaluations').catch(() => ({ data: { evaluations: [] } })),
        api.get('/performance-eval/manager/team-evaluations').catch(() => ({ data: { evaluations: [] } })),
      ]);

      // Merge — team-evaluations is the superset (contient aussi les pending). On dédoublonne par id.
      const seen = new Set();
      const performanceEvals = [];
      for (const ev of [...(teamPerfRes.data?.evaluations || []), ...(pendingRes.data?.evaluations || [])]) {
        if (ev?.id && !seen.has(ev.id)) {
          seen.add(ev.id);
          performanceEvals.push(ev);
        }
      }

      // Statuts considérés comme "complétés" côté manager (= visibles dans l'onglet Terminées
      // avec bouton PDF actif). On inclut TOUS les statuts post-évaluation manager, y compris
      // ceux en attente de validation RH ET les évals validées/clôturées.
      const COMPLETED_STATUSES = new Set([
        'manager_eval_done',
        'n2_validation_pending',
        'validated',
        'completed',
      ]);
      // Statuts pour lesquels le PDF est téléchargeable (RH a validé)
      const PDF_READY_STATUSES = new Set(['validated', 'completed']);
      
      // Build evaluations map from OLD response
      const evalsMap = {};
      const activeCampaigns = [];
      
      for (const campaign of campaignsWithParticipants) {
        const { participants, ...campaignData } = campaign;
        activeCampaigns.push(campaignData);
        evalsMap[campaign.id] = participants || [];
      }
      
      // Add performance evaluations to the map (group by campaign)
      for (const perfEval of performanceEvals) {
        const campaignId = perfEval.campaignId;
        
        // Check if campaign already exists
        if (!activeCampaigns.find(c => c.id === campaignId)) {
          activeCampaigns.push({
            id: campaignId,
            name: perfEval.campaignName || 'Campagne de performance',
            status: 'active',
            type: 'performance'
          });
        }
        
        if (!evalsMap[campaignId]) {
          evalsMap[campaignId] = [];
        }
        
        // Add participant to campaign
        evalsMap[campaignId].push({
          id: perfEval.id,
          employeeId: perfEval.employeeId,
          firstName: perfEval.employee?.firstName,
          lastName: perfEval.employee?.lastName,
          jobTitle: perfEval.employee?.jobTitle,
          photoUrl: perfEval.employee?.photoUrl,
          selfEvaluationStatus: 'completed', // Self-eval is done if status is manager_eval_pending
          selfStatus: 'completed',
          managerStatus: COMPLETED_STATUSES.has(perfEval.status) ? 'completed' : 'pending',
          rawStatus: perfEval.status,
          canDownloadPdf: PDF_READY_STATUSES.has(perfEval.status),
          selfScore: perfEval.selfScore,
          evaluationId: perfEval.id,
          objectives: perfEval.objectives,
          isPerformanceEval: true
        });
      }
      
      setCampaigns(activeCampaigns);
      setEvaluationsMap(evalsMap);
      
    } catch (error) {
      console.error('Error fetching evaluations:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.companyId]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Données actualisées');
  };
  
  // Handle evaluate
  const handleEvaluate = (member) => {
    // If it's a performance evaluation, navigate to the new form
    if (member.isPerformanceEval && member.evaluationId) {
      navigate(`/performance-eval/manager/evaluate/${member.evaluationId}`);
    } else {
      // Navigate to old campaign-based evaluation form
      navigate(`/performance/evaluate-campaign/${member.employeeId || member.id}`);
    }
  };
  
  // Filter evaluations by status
  const getPendingEvaluations = () => {
    const pending = [];
    Object.entries(evaluationsMap).forEach(([campaignId, participants]) => {
      const campaign = campaigns.find(c => c.id === campaignId);
      participants.forEach(p => {
        if (p.managerStatus !== 'completed') {
          pending.push({ ...p, campaign });
        }
      });
    });
    return pending;
  };
  
  const getCompletedEvaluations = () => {
    const completed = [];
    Object.entries(evaluationsMap).forEach(([campaignId, participants]) => {
      const campaign = campaigns.find(c => c.id === campaignId);
      participants.forEach(p => {
        if (p.managerStatus === 'completed') {
          completed.push({ ...p, campaign });
        }
      });
    });
    return completed;
  };
  
  // Role check
  if (user?.role !== 'Manager' && user?.role !== 'Admin' && user?.role !== 'SuperAdmin') {
    return (
      <DashboardLayout title="Évaluations">
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2 text-center">
            Accès réservé aux Managers
          </h2>
          <p className="text-slate-600 text-center max-w-md mb-6">
            Cette page est destinée aux managers d'équipe.
          </p>
          <Button onClick={() => navigate('/dashboard')}>
            Retour au tableau de bord
          </Button>
        </div>
      </DashboardLayout>
    );
  }
  
  if (loading || planLoading) {
    return (
      <DashboardLayout title="Évaluations">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }
  
  const pendingEvaluations = getPendingEvaluations();
  const completedEvaluations = getCompletedEvaluations();
  
  return (
    <DashboardLayout title="Évaluations">
      <div className="max-w-5xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Évaluations de mon équipe</h1>
            <p className="text-slate-600 mt-1">
              Gérez les évaluations de performance de vos collaborateurs
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
        
        {/* Stats cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{pendingEvaluations.length}</p>
                <p className="text-sm text-slate-500">À évaluer</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{completedEvaluations.length}</p>
                <p className="text-sm text-slate-500">Terminées</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{campaigns.length}</p>
                <p className="text-sm text-slate-500">Campagnes actives</p>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              À évaluer ({pendingEvaluations.length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Terminées ({completedEvaluations.length})
            </TabsTrigger>
          </TabsList>
          
          {/* Pending evaluations */}
          <TabsContent value="pending" className="mt-6">
            {pendingEvaluations.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {pendingEvaluations.map((evalItem, idx) => (
                  <TeamMemberEvalCard
                    key={evalItem.id || idx}
                    member={evalItem}
                    campaign={evalItem.campaign}
                    onEvaluate={handleEvaluate}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Toutes les évaluations sont terminées !
                  </h3>
                  <p className="text-slate-600">
                    Vous n'avez aucune évaluation en attente pour le moment.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Completed evaluations */}
          <TabsContent value="completed" className="mt-6">
            {completedEvaluations.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {completedEvaluations.map((evalItem, idx) => (
                  <TeamMemberEvalCard
                    key={evalItem.id || idx}
                    member={evalItem}
                    campaign={evalItem.campaign}
                    onEvaluate={handleEvaluate}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <ClipboardList className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Aucune évaluation terminée
                  </h3>
                  <p className="text-slate-600">
                    Les évaluations complétées apparaîtront ici.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        
        {/* Campaigns section */}
        {campaigns.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Campagnes actives
            </h2>
            {campaigns.map(campaign => (
              <CampaignSection
                key={campaign.id}
                campaign={campaign}
                participants={evaluationsMap[campaign.id] || []}
                onEvaluate={handleEvaluate}
              />
            ))}
          </div>
        )}
        
        {/* Empty state if no campaigns */}
        {campaigns.length === 0 && teamMembers.length > 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Target className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Aucune campagne active
              </h3>
              <p className="text-slate-600 max-w-md mx-auto">
                Il n'y a pas de campagne d'évaluation active pour le moment.
                L'Admin RH vous notifiera lorsqu'une nouvelle campagne sera lancée.
              </p>
            </CardContent>
          </Card>
        )}
        
        {/* Empty state if no team */}
        {teamMembers.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Aucun collaborateur dans votre équipe
              </h3>
              <p className="text-slate-600 max-w-md mx-auto">
                Les collaborateurs vous seront assignés par l'Admin RH.
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate('/manager-dashboard')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au cockpit
              </Button>
            </CardContent>
          </Card>
        )}
        
      </div>
    </DashboardLayout>
  );
}
